// ─────────────────────────────────────────────────────────────────────────────
// RETRAIT — tout ce fichier est temporaire. À la clôture du point 5 du plan
// docs/optimisation-lessons.md, supprimer la variante partout. La liste qui fait
// foi est la sortie de `grep -rn "perfVariant" src` ; les numéros de ligne
// ci-dessous sont indicatifs et vieillissent.
//
// Les points 3, 5 et 7 décrivent le retour à la variante A, en supposant qu'elle
// gagne la mesure. Si le point 5 retient au contraire un allègement, garder
// cette branche-là et supprimer seulement l'interrupteur qui la commandait.
//
// 1. src/utils/perfVariant.js          — supprimer le fichier entier.
// 2. src/utils/perfVariant.test.js     — supprimer le fichier entier.
// 3. src/utils/reverbBus.js            — retirer l'import et le test
//    `!bypassReverb()` de `getReverb`, qui redevient `if (!reverb)`. Le
//    contournement vit là depuis le passage en bus d'envoi : notePiano ne
//    construit plus de réverbération, il ne fait qu'attendre `whenReverbReady`.
// 4. src/utils/reverbBus.test.js       — retirer l'argument `variant` de
//    `loadBus` et le describe du contournement ; ne garder que la variante A.
// 5. src/components/GuitarNeckVisualizer.js — retirer l'import (l. 10) et le
//    terme `noLessonAnimations() && lessonMode` de `reducedMotion` (l. ~827),
//    qui redevient `isLightDevice()` seul.
// 6. src/components/GuitarNeckVisualizer.perfVariant.test.js — supprimer le
//    fichier entier.
// 7. src/components/LessonPlayer.js    — retirer l'import (l. 20), `barClass`
//    (l. ~390) et ses trois `className={barClass}` (l. ~394, ~407, ~420) : plus
//    aucune classe à poser sur les barres.
// 8. src/utils/lessonPerfTrace.js      — retirer l'import (l. 2) et le champ
//    `variant: perfVariant()` de la trace (l. ~320).
// 9. src/components/Settings.js        — retirer l'import de ce module et le
//    bloc encadré par `LAB POINT 5` (handlers + JSX des deux ListItem).
// 10. src/components/Settings.test.js  — retirer le `jest.mock` de ce module, le
//    `require` de `setPerfVariant` (hors marqueurs, suffixé `// LAB POINT 5`),
//    le `setPerfVariant.mockClear()` du `beforeEach` (même suffixe) et les deux
//    tests des interrupteurs Lab en fin de fichier. Attention, un grep sur
//    « perfVariant » rate `setPerfVariant` : chercher les deux.
// 11. scripts/build-measure.sh         : retirer `REACT_APP_PERF_LAB=true` de la
//    ligne `export`. C'est le seul chemin de build qui ouvre le Lab, aucun .env
//    ni aucun script de déploiement ne pose ce drapeau.
// 12. Le drapeau `REACT_APP_PERF_LAB`, `perfLabEnabled` et la purge de la clé
//    `upscale_perf_variant` faite au chargement disparaissent avec le fichier
//    (point 1). Avant de supprimer la purge, vérifier qu'aucun build diffusé
//    n'a pu écrire cette clé entre-temps, sinon la garder un temps ailleurs.
// 13. src/utils/reverbBus.test.js      : retirer aussi le `beforeAll`/`afterAll`
//    qui pose `process.env.REACT_APP_PERF_LAB` (suffixé `// LAB POINT 5`).
// ─────────────────────────────────────────────────────────────────────────────

// Variante de diagnostic du point 5 (coût des animations et de la réverbération).
// Purement locale : aucune préférence synchronisée, seulement deux interrupteurs
// temporaires dans les réglages (Lab).
//
// La clé est lue UNE SEULE FOIS au chargement du module. Changer de variante
// demande donc un rechargement de la page, ce qui est voulu : une commutation à
// chaud laisserait des nœuds audio à moitié reconstruits et fausserait la mesure.

const STORAGE_KEY = 'upscale_perf_variant';
const VARIANTS = ['A', 'B', 'C', 'D'];

// Le Lab n'existe que dans un build de mesure (scripts/build-measure.sh). Tout
// build diffusé, web comme iOS ou Android, part sans ce drapeau : la variante y
// est figée sur A et les interrupteurs ne sont pas rendus.
// La forme `process.env.REACT_APP_PERF_LAB` est littérale à dessein, c'est la
// seule que webpack remplace à la compilation. Un seul point de lecture, ici.
const labEnabled = process.env.REACT_APP_PERF_LAB === 'true';

export const perfLabEnabled = () => labEnabled;

const read = () => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return 'A';
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (typeof raw !== 'string') return 'A';
    const upper = raw.trim().toUpperCase();
    return VARIANTS.includes(upper) ? upper : 'A';
  } catch (e) {
    // Stockage indisponible (mode privé, iframe) : variante de référence.
    return 'A';
  }
};

// Hors Lab, la clé est effacée au chargement : une préférence expérimentale
// posée par un build de mesure ne doit pas continuer à agir en silence. Seule
// cette clé est touchée, les réglages de la personne ne bougent pas.
const purge = () => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    window.localStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    // Stockage indisponible : rien à purger.
  }
};

const resolve = () => {
  if (!labEnabled) {
    purge();
    return 'A';
  }
  return read();
};

const variant = resolve();

export const perfVariant = () => variant;

// B et D : transitions et animations neutralisées dans le périmètre de la leçon.
export const noLessonAnimations = () => variant === 'B' || variant === 'D';

// C et D : aucun nœud de réverbération n'est bâti dans toute l'app, les envois
// des sources ne sont connectés à rien et il ne reste que le signal direct.
export const bypassReverb = () => variant === 'C' || variant === 'D';

// Rechargement de la page, isolé pour que les tests le remplacent.
export const reloadApp = () => {
  window.location.reload();
};

// Indirection obligatoire : la transpilation CRA fige les appels internes sur la
// liaison locale, un `jest.spyOn` sur le module ne les intercepterait pas. Les
// tests remplacent `perfVariantInternals.reloadApp`.
export const perfVariantInternals = { reloadApp };

// Bascule la variante depuis l'interface puis recharge, car :
// - la clé n'est lue qu'une fois, au chargement de ce module ;
// - le graphe audio du piano (notePiano.js) est bâti une fois, avec ou sans
//   réverbération selon la variante lue à ce moment-là ;
// - il n'existe donc aucune commutation à chaud : sans rechargement, l'interface
//   mentirait sur ce que l'app fait réellement.
export const setPerfVariant = ({ animations, reverb }) => {
  // Hors Lab, aucun interrupteur n'est rendu : si un appel arrive quand même,
  // il ne doit ni écrire la clé ni recharger la page.
  if (!labEnabled) return;

  const letter = animations ? (reverb ? 'A' : 'C') : (reverb ? 'B' : 'D');

  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      if (letter === 'A') {
        window.localStorage.removeItem(STORAGE_KEY);
      } else {
        window.localStorage.setItem(STORAGE_KEY, letter);
      }
    }
  } catch (e) {
    // Stockage indisponible : le rechargement repartira sur la variante A.
  }

  perfVariantInternals.reloadApp();
};
